export type ZiaMezaLinkKind = "site" | "project" | "feed" | "contact" | "donate" | "creator";

export type ZiaMezaLink = {
    key: string;
    label: string;
    href: string;
    kind: ZiaMezaLinkKind;
    external: boolean;
    rel?: string;
    audience?: "general" | "league-of-legends";
};

export type ZiaMezaActionLink = Omit<ZiaMezaLink, "href"> & {
    href: string | null;
    configured: boolean;
};

export type ConfiguredZiaMezaActionLink = ZiaMezaActionLink & {
    href: string;
    configured: true;
};

// Central public ZiaMeza identity/link registry for Ziarcadia and embedded project surfaces.
export const ZIAMEZA_IDENTITY = {
    brandName: "ZiaMeza",
    displayDomain: "ZiaMeza.com",
    homepageUrl: "https://ziameza.com/",
    contactEmail: "ziameza@gmail.com"
} as const;

export const ZIAMEZA_LINKS = {
    home: {
        key: "home",
        label: ZIAMEZA_IDENTITY.displayDomain,
        href: ZIAMEZA_IDENTITY.homepageUrl,
        kind: "site",
        external: true,
        rel: "noreferrer"
    },
    ziarcadia: {
        key: "ziarcadia",
        label: "Ziarcadia",
        href: "https://ziarcadia.com/",
        kind: "project",
        external: true,
        rel: "noreferrer"
    },
    master1000: {
        key: "master1000",
        label: "Zia's Master 1000",
        href: "https://master1000.ziameza.com/",
        kind: "project",
        external: true,
        rel: "noreferrer"
    },
    master1000Devlog: {
        key: "master1000-devlog",
        label: "Zia's Master 1000 devlog",
        href: "https://ziameza.itch.io/master1000/devlog",
        kind: "project",
        external: true,
        rel: "noreferrer"
    },
    master1000PatchNotesRss: {
        key: "master1000-patch-notes-rss",
        label: "Zia's Master 1000 patch notes RSS",
        href: "https://ziameza.itch.io/master1000/devlog.rss",
        kind: "feed",
        external: true,
        rel: "noreferrer"
    }
} as const satisfies Record<string, ZiaMezaLink>;

export const ZIAMEZA_ACTION_LINKS = {
    contact: {
        key: "contact",
        label: "Contact",
        href: `mailto:${ZIAMEZA_IDENTITY.contactEmail}`,
        kind: "contact",
        external: false,
        configured: true
    },
    donate: {
        key: "donate",
        label: "Donate",
        href: null,
        kind: "donate",
        external: true,
        rel: "noreferrer",
        configured: false
    }
} as const satisfies Record<string, ZiaMezaActionLink>;

export const ZIAMEZA_LEAGUE_CREATOR_PROFILE = {
    handle: "AquaDragon",
    summary: "Small-time League of Legends off-meta enthusiast and content creator.",
    signatureBuilds: [
        "Malzahar Jungle",
        "Tank Karthus",
        "Top Jinx",
        "Support Orianna",
        "AP Kog'maw",
        "Bot Vel'koz"
    ]
} as const;

export const ZIAMEZA_CREATOR_LINKS = {
    twitch: {
        key: "aquadragon-twitch",
        label: "Twitch",
        href: "https://www.twitch.tv/aquadragon33",
        kind: "creator",
        external: true,
        rel: "noreferrer",
        audience: "league-of-legends"
    },
    youtubeStreams: {
        key: "aquadragon-youtube-streams",
        label: "YouTube streams",
        href: "https://www.youtube.com/@AquaDragon/streams",
        kind: "creator",
        external: true,
        rel: "noreferrer",
        audience: "league-of-legends"
    },
    mobafireBuilds: {
        key: "aquadragon-mobafire-builds",
        label: "MOBAFire builds",
        href: "https://www.mobafire.com/profile/aqua-dragon-242946/content/builds",
        kind: "creator",
        external: true,
        rel: "noreferrer",
        audience: "league-of-legends"
    }
} as const satisfies Record<string, ZiaMezaLink>;

const ZIAMEZA_ACTION_LINK_LIST: readonly ZiaMezaActionLink[] = Object.values(ZIAMEZA_ACTION_LINKS);

export const getConfiguredZiaMezaActionLinks = (): ConfiguredZiaMezaActionLink[] =>
    ZIAMEZA_ACTION_LINK_LIST.filter(
        (link): link is ConfiguredZiaMezaActionLink =>
            link.configured && typeof link.href === "string" && link.href.length > 0
    );
